import { create } from 'zustand';
import { AxiosError } from 'axios';
import { getCollectionSites } from '../services/collection-site.service';
import {
  getCollectionSiteStatistics,
  patchCollectionSiteStatistics,
} from '../services/collection-site-statistics.service';
import { CollectionSiteSchema } from '../types/collection-site.type';
import {
  CollectionSiteStatisticsSchema,
  PatchCollectionSiteStatisticsRequest,
} from '../types/collection-site-statistics.type';
import { ScopeEmissionsSummary } from '../types/utility-emission.type';

const emptyScopeEmissions = (): ScopeEmissionsSummary => ({
  scope_1_tco2e: 0,
  scope_2_tco2e: 0,
  scope_1_complete: true,
  scope_2_complete: true,
  missing_factor_utility_keys: [],
  activity_lines: [],
});

const STORAGE_KEY = 'dataEntryPreferences';

interface GenericResponse<T> {
  error?: { message?: string; code?: string } | null;
  response?: { message?: string; code?: string } | null;
  result: T;
}

function parseYearMonth(yyyyMm: string): { year: number; month: number } {
  const [y, m] = yyyyMm.split('-').map(Number);
  return { year: y, month: m };
}

function monthStartIso(year: number, month: number): string {
  const mm = String(month).padStart(2, '0');
  return `${year}-${mm}-01T00:00:00`;
}

function emptyStatisticsRow(
  collectionSiteId: string,
  year: number,
  month: number
): CollectionSiteStatisticsSchema {
  return {
    id: '',
    collection_site_id: collectionSiteId,
    month_date: monthStartIso(year, month),
    water_consumption: 0,
    water_amount_sar: 0,
    electricity_consumption_kwh: 0,
    electricity_amount_sar: 0,
    oil_consumption_liters: 0,
    oil_amount_sar: 0,
    gas_consumption_m3: 0,
    gas_amount_sar: 0,
    utility_activities: [],
    scope_emissions: emptyScopeEmissions(),
    data: {},
    created_at: '',
    updated_at: '',
  };
}

function sortSites(sites: CollectionSiteSchema[]): CollectionSiteSchema[] {
  return [...sites].sort((a, b) => a.id.localeCompare(b.id));
}

function loadStoredPreferences(): { selectedSiteId: string | null; selectedMonth: string | null } {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { selectedSiteId: null, selectedMonth: null };
    const parsed = JSON.parse(raw) as { selectedSiteId?: string; selectedMonth?: string };
    return {
      selectedSiteId: parsed.selectedSiteId ?? null,
      selectedMonth: parsed.selectedMonth ?? null,
    };
  } catch {
    return { selectedSiteId: null, selectedMonth: null };
  }
}

const defaultMonth = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

interface DataEntryState {
  sites: CollectionSiteSchema[];
  selectedSiteId: string | null;
  selectedMonth: string;
  statistics: CollectionSiteStatisticsSchema | null;
  /** True when the last GET returned 404 (no row yet for this site/month). */
  noRowYet: boolean;
  isLoadingSites: boolean;
  isLoadingStats: boolean;
  isSaving: boolean;
  error: string | null;
  clearError: () => void;
  fetchSites: (organizationId: string) => Promise<void>;
  setSelectedSiteId: (siteId: string | null) => void;
  setSelectedMonth: (yyyyMm: string) => void;
  fetchStatistics: (organizationId: string, siteId: string, yyyyMm: string) => Promise<void>;
  patchStatistics: (
    organizationId: string,
    siteId: string,
    yyyyMm: string,
    body: PatchCollectionSiteStatisticsRequest
  ) => Promise<boolean>;
  loadPreferences: () => void;
  savePreferences: () => void;
  resetForOrganizationChange: () => void;
}

export const useDataEntryStore = create<DataEntryState>((set, get) => ({
  sites: [],
  selectedSiteId: null,
  selectedMonth: defaultMonth(),
  statistics: null,
  noRowYet: false,
  isLoadingSites: false,
  isLoadingStats: false,
  isSaving: false,
  error: null,

  clearError: () => set({ error: null }),

  loadPreferences: () => {
    const { selectedSiteId, selectedMonth } = loadStoredPreferences();
    set({
      ...(selectedMonth ? { selectedMonth } : {}),
      ...(selectedSiteId ? { selectedSiteId } : {}),
    });
  },

  savePreferences: () => {
    const { selectedSiteId, selectedMonth } = get();
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ selectedSiteId, selectedMonth })
    );
  },

  resetForOrganizationChange: () => {
    set({
      sites: [],
      selectedSiteId: null,
      statistics: null,
      noRowYet: false,
      error: null,
    });
  },

  setSelectedSiteId: (siteId) => {
    set({ selectedSiteId: siteId });
    get().savePreferences();
  },

  setSelectedMonth: (yyyyMm) => {
    set({ selectedMonth: yyyyMm });
    get().savePreferences();
  },

  fetchSites: async (organizationId) => {
    set({ isLoadingSites: true, error: null });
    try {
      const response = await getCollectionSites({ organization_id: organizationId });
      const data = response as GenericResponse<CollectionSiteSchema[]>;
      const sites = sortSites(data.result ?? []);
      const prefs = loadStoredPreferences();
      const preferredId = prefs.selectedSiteId;
      const validPreferred =
        preferredId && sites.some((s) => s.id === preferredId) ? preferredId : null;
      const nextSelected = validPreferred ?? (sites[0]?.id ?? null);

      set({
        sites,
        selectedSiteId: nextSelected,
        isLoadingSites: false,
      });
      get().savePreferences();
    } catch (error) {
      const message =
        error instanceof AxiosError
          ? (error.response?.data as { detail?: string })?.detail ||
            error.message
          : 'Failed to load branches';
      set({ sites: [], selectedSiteId: null, isLoadingSites: false, error: String(message) });
    }
  },

  fetchStatistics: async (organizationId, siteId, yyyyMm) => {
    set({ isLoadingStats: true, error: null, noRowYet: false, statistics: null });
    const { year, month } = parseYearMonth(yyyyMm);
    try {
      const response = await getCollectionSiteStatistics(organizationId, siteId, {
        year,
        month,
      });
      const data = response as GenericResponse<CollectionSiteStatisticsSchema>;
      set({
        statistics: data.result,
        noRowYet: false,
        isLoadingStats: false,
      });
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        set({
          statistics: emptyStatisticsRow(siteId, year, month),
          noRowYet: true,
          isLoadingStats: false,
        });
        return;
      }
      const message =
        error instanceof AxiosError
          ? (error.response?.data as { detail?: string })?.detail ||
            error.message
          : 'Failed to load statistics';
      set({
        statistics: null,
        noRowYet: false,
        isLoadingStats: false,
        error: String(message),
      });
    }
  },

  patchStatistics: async (organizationId, siteId, yyyyMm, body) => {
    const keys = Object.keys(body);
    if (keys.length === 0) {
      set({ error: 'Nothing to save' });
      return false;
    }
    const { year, month } = parseYearMonth(yyyyMm);
    set({ isSaving: true, error: null });
    try {
      const response = await patchCollectionSiteStatistics(
        organizationId,
        siteId,
        { year, month },
        body
      );
      const data = response as GenericResponse<CollectionSiteStatisticsSchema>;
      set({
        statistics: data.result,
        noRowYet: false,
        isSaving: false,
      });
      return true;
    } catch (error) {
      const message =
        error instanceof AxiosError
          ? (error.response?.data as { detail?: string })?.detail ||
            error.message
          : 'Failed to save';
      set({ isSaving: false, error: String(message) });
      return false;
    }
  },
}));
